/**
 * 🌤️ TODAY WEATHER BAND COMPONENT
 * Displays today's weather in the "Today's Focus" section and on workout cards
 * 
 * Mission 14: Weather & Climate Intelligence - Phase 3 Frontend
 * Integrates with backend weather cache (no additional API calls)
 */

import React, { useState, useEffect } from 'react';
import { WeatherBand } from './WeatherIcon';
import { storage } from '../services/storage';
// Using relative URL - works with proxy or same-origin

interface TodayWeatherData {
  temp_c: number | null;
  condition: string;
  condition_icon: string;
  precipitation_mm: number;
  wind_kmh: number;
  humidity: number;
  aqi: number | null;
  sunrise: string | null;
  sunset: string | null;
}

interface WeatherCacheResponse {
  today: TodayWeatherData;
  forecast_7day: Array<{
    date: string;
    temp_high_c: number;
    temp_low_c: number;
    condition: string;
  }>;
  cached_at: string;
  expires_at: string;
}

/**
 * TodayWeatherBand Component
 * 
 * Fetches and displays today's weather from backend cache
 * 
 * @example
 * ```tsx
 * // In Today's Focus section:
 * <TodayWeatherBand size="md" className="mb-4" />
 * 
 * // On workout card:
 * <TodayWeatherBand size="sm" date={workout.scheduledDate} />
 * ```
 */
export const TodayWeatherBand: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  date?: string | Date; // Optional: show forecast for specific date
  className?: string;
}> = ({
  size = 'sm',
  date,
  className = ''
}) => {
  const [weatherData, setWeatherData] = useState<WeatherCacheResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      const jwtToken = await storage.getItem('jwt_token');
      if (!jwtToken) {
        setLoading(false);
        return;
      }

      try {
        // Use relative URL - works with proxy or same-origin (original working approach)
        const response = await fetch('/api/weather/cached-forecast', {
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 404) {
          // No weather data yet (location not confirmed or cache expired)
          setLoading(false);
          return;
        }

        if (!response.ok) {
          // Check if response is HTML (error page) instead of JSON
          const contentType = response.headers.get('content-type');
          if (contentType && !contentType.includes('application/json')) {
            // Suppress console warnings for expected 404s (location not confirmed)
            setLoading(false);
            return; // Graceful degradation - don't show weather
          }
          // Only log non-404 errors
          if (response.status !== 404) {
            console.error(`Weather API error: ${response.status}`);
          }
          setLoading(false);
          return;
        }

        // Verify response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Suppress console warnings for expected 404s
          setLoading(false);
          return; // Graceful degradation
        }

        const data: WeatherCacheResponse = await response.json();
        setWeatherData(data);
        setError(null);
      } catch (err) {
        // Suppress 404 errors (expected when location not confirmed)
        if (!(err instanceof Error && err.message.includes('404'))) {
          console.error('Weather fetch error:', err);
        }
        // Don't set error state - just gracefully degrade (don't show weather)
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []); // Empty dependency array - JWT check happens inside effect

  // Don't show anything if still loading or has error (graceful degradation)
  // But show weather if we have data, even if loading is true (data might be cached)
  if (error) {
    return null;
  }
  
  // If loading and no data yet, don't show (wait for data)
  if (loading && !weatherData) {
    return null;
  }
  
  // If no data after loading completes, don't show
  if (!loading && !weatherData) {
    return null;
  }

  const data = weatherData;
  if (!data) return null;

  // Determine which weather to show
  let weatherToShow: {
    condition: string;
    tempHigh?: number;
    tempLow?: number;
  };

  if (date) {
    // Show forecast for specific date
    const targetDate = typeof date === 'string' ? new Date(date) : date;
    const targetDateString = targetDate.toISOString().split('T')[0];
    
    const forecast = data.forecast_7day?.find(
      day => day.date === targetDateString
    );
    
    if (forecast) {
      weatherToShow = {
        condition: forecast.condition,
        tempHigh: forecast.temp_high_c,
        tempLow: forecast.temp_low_c
      };
    } else {
      // Forecast not available for this date - silently return null
      // (workouts beyond 6-day forecast window won't show weather)
      return null;
    }
  } else {
    // Show today's weather
    weatherToShow = {
      condition: data.today.condition,
      tempHigh: data.today.temp_c || undefined,
      tempLow: undefined
    };
  }

  return (
    <WeatherBand
      condition={weatherToShow.condition}
      tempHigh={weatherToShow.tempHigh}
      tempLow={weatherToShow.tempLow}
      size={size}
      className={className}
    />
  );
};

/**
 * WeatherSummaryBand Component
 * 
 * Larger weather summary with additional details for the Today section header
 * 
 * @example
 * ```tsx
 * <WeatherSummaryBand />
 * ```
 */
export const WeatherSummaryBand: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  const [weatherData, setWeatherData] = useState<WeatherCacheResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      const jwtToken = await storage.getItem('jwt_token');
      if (!jwtToken) {
        setLoading(false);
        return;
      }

      try {
        // Use relative URL - works with proxy or same-origin (original working approach)
        const response = await fetch('/api/weather/cached-forecast', {
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 404) {
          setLoading(false);
          return;
        }

        if (!response.ok) {
          // Suppress 404 errors (expected when location not confirmed)
          if (response.status === 404) {
            setLoading(false);
            return;
          }
          // Check if response is HTML (error page) instead of JSON
          const contentType = response.headers.get('content-type');
          if (contentType && !contentType.includes('application/json')) {
            // Suppress console warnings for expected 404s
            setLoading(false);
            return; // Graceful degradation
          }
          // Only log non-404 errors
          console.error(`Weather API error: ${response.status}`);
          setLoading(false);
          return;
        }

        // Verify response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Suppress console warnings for expected 404s
          setLoading(false);
          return; // Graceful degradation
        }

        const data: WeatherCacheResponse = await response.json();
        setWeatherData(data);
      } catch (err) {
        console.error('Weather fetch error:', err);
        // Graceful degradation - don't show weather
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []); // Empty dependency array - JWT check happens inside effect

  if (loading || !weatherData) {
    return null;
  }

  const { today } = weatherData;
  
  return (
    <div className={`
      flex items-center gap-3 
      bg-gradient-to-r from-blue-50 to-cyan-50 
      rounded-lg px-4 py-3 
      border border-blue-100
      ${className}
    `}>
      <TodayWeatherBand size="md" />
      
      <div className="flex-1 flex items-center gap-4 text-sm text-gray-600">
        {today.precipitation_mm > 0 && (
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 9.293a1 1 0 011.414 0L10 12.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>{today.precipitation_mm.toFixed(1)}mm</span>
          </div>
        )}
        
        {today.wind_kmh > 15 && (
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <span>{Math.round(today.wind_kmh)} km/h</span>
          </div>
        )}
        
        {today.aqi && today.aqi > 100 && (
          <div className="flex items-center gap-1 text-orange-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-medium">AQI {today.aqi}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayWeatherBand;
